/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import { submitRequestDialogStyles as styles } from "../../styles";
import { useGetEnvs } from "../../../../api/envs";
import {
  useListALLCommonScripts,
  useSubmitOsBuild,
} from "../../../../api/softwareImages";
import ScriptItem from "./ScriptItem";
import CloudSpecificItem from "./CloudSpecificItem";
import DiscardChangesDialog from "../DiscardChangesDialog";
import useResizableDrawerWidth from "../../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../../components/DrawerSnapToggle";

const CONTEXT_FIELDS = [
  ["Engine", "engine"],
  ["OS Name", "osName"],
  ["Old Version", "oldVersion"],
  ["Old Version Name", "oldVersionName"],
  ["Release Branch", "releaseBranch"],
];

const SubmitOsPatchRequestDialog = ({
  open,
  resetDialog,
  callback,
  displayNotification,
  initPayload,
  availableClouds,
}) => {
  const [payload, setPayload] = useState({});
  const [baseline, setBaseline] = useState("");
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isWideEnough = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "submit-os-patch-request",
    defaultWidth: 700,
    maxWidth: 1000,
    enabled: isWideEnough,
  });

  const {
    postData: submitOsBuild,
    response: submitOsBuildResp,
    responseStatus: submitOsBuildStatus,
    isLoading: isSubmitting,
  } = useSubmitOsBuild();

  const { postData: listAllCommonScripts, response: listAllCommonScriptsResp } =
    useListALLCommonScripts(initPayload?.releaseBranch);

  const { postData: getEnvs, response: getEnvsResp } = useGetEnvs();

  useEffect(() => {
    if (open) {
      getEnvs();
      const init = structuredClone(initPayload);
      setPayload(init);
      setBaseline(JSON.stringify(init));
      listAllCommonScripts();
    } else {
      setPayload({});
    }
    setConfirmDiscardOpen(false);
  }, [open, initPayload]);

  const isDirty = useMemo(
    () => open && JSON.stringify(payload) !== baseline,
    [open, payload, baseline]
  );

  // Backdrop click, Esc, × and Cancel all come through here; a dirty form
  // asks for confirmation before discarding.
  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      resetDialog();
    }
  };

  const handleDialogConfirm = () => {
    const reqPayload = structuredClone(payload);
    availableClouds?.forEach((cloud) => {
      if (reqPayload?.tenant?.[cloud.toUpperCase()] === "No Tenant") {
        delete reqPayload.tenant[cloud.toUpperCase()];
      }
    });
    delete reqPayload.dbSoftware;
    submitOsBuild(reqPayload);
  };

  useEffect(() => {
    displayNotification(submitOsBuildResp, submitOsBuildStatus, callback);
    if (
      typeof submitOsBuildStatus === "number" &&
      submitOsBuildStatus >= 200 &&
      submitOsBuildStatus < 300
    ) {
      resetDialog();
    }
  }, [submitOsBuildResp, submitOsBuildStatus]);

  // One editable field per row; the old value shows as a right-aligned hint.
  const renderField = (label, key, wasValue) => (
    <Box sx={styles.fieldRow}>
      <Box sx={styles.labelRow}>
        <FormLabel required sx={styles.fieldLabel}>
          {label}
        </FormLabel>
        {wasValue && (
          <Typography sx={styles.wasHint}>was {wasValue}</Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        sx={styles.fieldInput}
        value={payload?.[key] || ""}
        onChange={(e) =>
          setPayload((prev) => ({ ...prev, [key]: e.target.value }))
        }
      />
    </Box>
  );

  const setScriptDetails = (idx) => {
    return (scriptDetails) => {
      setPayload((prev) => ({
        ...prev,
        scripts: [
          ...prev?.scripts?.slice(0, idx),
          scriptDetails,
          ...prev?.scripts?.slice(idx + 1),
        ],
      }));
    };
  };

  const deleteScript = (idx) => {
    return () => {
      setPayload((prev) => ({
        ...prev,
        scripts: [
          ...prev?.scripts?.slice(0, idx),
          ...prev?.scripts?.slice(idx + 1),
        ],
      }));
    };
  };

  useEffect(() => {
    displayNotification(submitOsBuildResp, submitOsBuildStatus, callback);
  }, [submitOsBuildResp, submitOsBuildStatus]);

  const isValidPayload = useMemo(() => {
    if (!payload?.jiraId || !payload?.version || !payload?.versionName) {
      return false;
    }
    const payloodClouds = Object?.keys(payload?.clouds || {});
    const cloudVaildation =
      payloodClouds?.length &&
      payloodClouds?.every((cloud) => {
        const item = payload?.clouds?.[cloud];
        return item?.tenantDomain && item?.dbSoftware?.length;
      });
    if (!cloudVaildation) {
      return false;
    }

    return (
      payload?.scripts?.length &&
      payload?.scripts?.every((scriptItem) => scriptItem?.scriptName)
    );
  }, [payload]);

  // Footer summary: what's still missing before Confirm enables.
  const validationSummary = useMemo(() => {
    const missing = [];
    if (!payload?.version) missing.push("new version");
    if (!payload?.versionName) missing.push("new name");
    if (!payload?.jiraId) missing.push("Jira Id");
    const selectedClouds = Object.keys(payload?.clouds || {});
    if (!selectedClouds.length) {
      missing.push("at least one cloud");
    } else if (
      !selectedClouds.every((cloud) => {
        const item = payload?.clouds?.[cloud];
        return item?.tenantDomain && item?.dbSoftware?.length;
      })
    ) {
      missing.push("tenant & DB software for each selected cloud");
    }
    if (!payload?.scripts?.length) {
      missing.push("at least one script");
    } else if (
      !payload?.scripts?.every((scriptItem) => scriptItem?.scriptName)
    ) {
      missing.push("a name for every script");
    }
    if (!missing.length) return "";
    return `${missing.length} item${
      missing.length > 1 ? "s" : ""
    } left: ${missing.join(", ")}`;
  }, [payload]);

  const runningTenants = useMemo(
    () =>
      getEnvsResp?.response?.filter((tenant) => tenant?.status === "RUNNING") ||
      [],
    [getEnvsResp]
  );

  const addScript = () =>
    setPayload((prev) => ({
      ...prev,
      scripts: [
        ...(prev?.scripts || []),
        { cloud: "ALL", rebootTime: 0, scriptName: "" },
      ],
    }));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      PaperProps={{
        sx: { ...styles.drawerPaper(drawerWidth), overflow: "visible" },
      }}
    >
      <DrawerResizeHandle
        {...resizeHandleProps}
        ariaLabel="Resize submit OS patch request drawer"
      />
      <DrawerSnapToggle
        enabled={isWideEnough}
        isWide={isDrawerWide}
        onClick={toggleDrawerWide}
        ariaLabel="Expand submit OS patch request drawer to 75% width"
        collapseLabel="Collapse submit OS patch request drawer"
        sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
      />
      <Box sx={styles.drawerHeader}>
        <Box>
          <Typography sx={styles.title}>Submit OS Patch Request</Typography>
          <Typography sx={styles.subtitle}>
            {`Patching ${
              initPayload?.oldVersionName || initPayload?.osName || ""
            }`}
          </Typography>
        </Box>
        <IconButton sx={styles.closeButton} onClick={requestClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.drawerBody}>
        <Box sx={styles.contextBlock}>
          {CONTEXT_FIELDS.map(([label, key]) => (
            <Box key={key}>
              <Typography sx={styles.contextLabel}>{label}</Typography>
              <Typography sx={styles.contextValue}>
                {initPayload?.[key] || "—"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={styles.sectionHeading}>New OS Version</Typography>
        {renderField("Version", "version", initPayload?.oldVersion)}
        {renderField(
          "Version Name",
          "versionName",
          initPayload?.oldVersionName
        )}
        {renderField("Jira Id", "jiraId")}

        <Divider sx={styles.sectionDivider} />

        <Typography sx={styles.sectionHeading}>Build For</Typography>
        <Box sx={styles.cloudCardsGrid}>
          {availableClouds?.map((cloud, i) => (
            <CloudSpecificItem
              payload={payload}
              setPayload={setPayload}
              key={i}
              cloud={cloud}
              runningTenants={runningTenants}
            />
          ))}
        </Box>

        <Divider sx={styles.sectionDivider} />

        <Box sx={styles.sectionHeadRow}>
          <Typography sx={{ ...styles.sectionHeading, mb: 0 }}>
            Scripts
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            sx={styles.selectAllButton}
            onClick={addScript}
          >
            Add script
          </Button>
        </Box>
        {payload?.scripts?.length ? (
          payload.scripts.map((scriptDetails, i) => (
            <ScriptItem
              key={i}
              listAllCommonScriptsResp={listAllCommonScriptsResp}
              setScriptDetails={setScriptDetails(i)}
              scriptDetails={scriptDetails}
              deleteScript={deleteScript(i)}
            />
          ))
        ) : (
          <Box sx={styles.scriptsEmpty}>
            No scripts added yet — at least one is required.
          </Box>
        )}
      </Box>

      <Box sx={styles.drawerFooter}>
        <Typography sx={styles.footerSummary(!isValidPayload)}>
          {isValidPayload
            ? "All required fields are filled."
            : validationSummary}
        </Typography>
        <Box sx={styles.actionButtons}>
          <Button
            variant="outlined"
            color="inherit"
            sx={styles.actionButton}
            onClick={requestClose}
          >
            Cancel
          </Button>
          <Tooltip title={isValidPayload ? "" : validationSummary}>
            <span>
              <Button
                variant="contained"
                sx={styles.actionButton}
                disabled={!isValidPayload || isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : null
                }
                onClick={handleDialogConfirm}
              >
                Confirm
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onKeepEditing={() => setConfirmDiscardOpen(false)}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          resetDialog();
        }}
      />
    </Drawer>
  );
};

export default SubmitOsPatchRequestDialog;

export const useSubmitOsPatchRequestDialog = (
  callback = () => {},
  displayNotification = () => {}
) => {
  const [open, setOpen] = useState(false);
  const [initPayload, setInitPayload] = useState({});
  const [availableClouds, setAvailableClouds] = useState([]);

  return {
    submitOsPatchRequestDialog: (
      <SubmitOsPatchRequestDialog
        open={open}
        resetDialog={() => setOpen(false)}
        callback={callback}
        displayNotification={displayNotification}
        initPayload={initPayload}
        availableClouds={availableClouds}
      />
    ),
    setOpenSubmitOsPatchRequestDialog: (initPayload, availableClouds) => {
      setOpen(true);
      setInitPayload(initPayload || {});
      setAvailableClouds(availableClouds || []);
    },
  };
};
